/**
 * The Code tab's pure logic: statuses in words, which files may be attached,
 * and who sees the tab. In `lib/` so `npm test -w web` can check it (node's
 * runner cannot parse the JSX that draws it).
 *
 * The type list and the 10 MB limit mirror api/src/lib/codeRequests.js. The
 * server is the authority and checks magic bytes; this only saves a doomed
 * upload and gives a plain sentence.
 */

export const STATUS_LABEL = {
  QUEUED: 'Waiting to be built',
  BUILDING: 'Being built',
  DEPLOYING: 'Going live',
  DONE: 'Done, live now',
  NEEDS_APPROVAL: 'Built, held until a person releases it',
  NEEDS_INPUT: 'Needs an answer from you',
  FAILED: 'Could not be completed',
  CANCELLED: 'Cancelled',
};

export const IN_FLIGHT = ['QUEUED', 'BUILDING', 'DEPLOYING'];

/** An unknown status reads as itself rather than as "undefined". */
export const statusLabel = (status) =>
  (Object.hasOwn(STATUS_LABEL, status) ? STATUS_LABEL[status] : String(status ?? ''));

export const isInFlight = (status) => IN_FLIGHT.includes(status);

/** Only a request that has not started building can be withdrawn. */
export const canCancel = (status) => status === 'QUEUED' || status === 'NEEDS_INPUT';

export const UPLOAD_TYPES = [
  'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf', 'text/plain', 'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** null when the file may be attached, else the sentence to show. */
export function uploadProblem(file) {
  const name = file?.name || 'That file';
  if (!file || !UPLOAD_TYPES.includes(file.type)) return `${name}: that kind of file cannot be attached`;
  if (file.size > MAX_UPLOAD_BYTES) return `${name} is larger than 10 MB`;
  if (file.size === 0) return `${name} is empty`;
  return null;
}

/** The Code entry is drawn only when the server said so; no role or capability is involved. */
export const showCodeTab = (session) => session?.codeTab === true;

/** An image is shown inline (from a fetched blob); everything else is a download. */
export const isImage = (contentType) => typeof contentType === 'string' && contentType.startsWith('image/');
